import { Dialog } from 'radix-ui';
import type { ReactNode } from 'react';

import { cx } from '../../lib/cx';
import styles from './modal.module.css';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Пояснение под заголовком. Для подтверждений — то, что именно произойдёт. */
  description?: ReactNode;
  children?: ReactNode;
  /** Кнопки действий. Порядок — «отмена, затем подтверждение». */
  footer?: ReactNode;
  className?: string;
}

/**
 * Модальное окно поверх Radix Dialog.
 *
 * Radix, а не своя реализация: ловушка фокуса, возврат фокуса на триггер,
 * `aria-modal`, Esc и блокировка прокрутки фона — это набор, который написать
 * заново правильно почти невозможно, а ошибки в нём не видны глазами.
 *
 * Заголовок обязателен: `Dialog.Title` — это то, что скринридер зачитывает при
 * открытии, и диалог без него оказывается безымянным.
 *
 * Крестик закрытия — ВСЕГДА, без флага. Radix закрывает диалог по Esc и по тапу вне
 * окна, и этого достаточно на клавиатуре, но не пальцем: на телефоне «тапнуть мимо»
 * означает попасть в узкую полосу у края экрана, а Esc там нет вовсе. Диалог без
 * видимого выхода на тач-устройстве читается как тупик.
 *
 * Флага «без крестика» нет намеренно: единственный аргумент за него — «человек обязан
 * выбрать одно из действий», а такому диалогу и тап вне окна с Esc пришлось бы
 * запрещать. Пока это не потребовалось, выход есть у каждого окна.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={cx(styles.content, className)}>
          <div className={styles.head}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>

            {/* Метка зашита по-русски, как в остальных компонентах пакета
                (см. `Pagination`): словари в `src/i18n` есть, но к компонентам
                не подключены, и подключать их ради одной кнопки значило бы вводить
                новый механизм в обход существующего соглашения. */}
            <Dialog.Close className={styles.close} aria-label="Закрыть">
              ×
            </Dialog.Close>
          </div>
          {description ? (
            <Dialog.Description className={styles.description}>{description}</Dialog.Description>
          ) : null}
          {children ? <div className={styles.body}>{children}</div> : null}
          {footer ? <div className={styles.footer}>{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
